/**
 * features/hr/BulkPayslips.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — bulk payslip printing by cohort (staff category).
 *
 * Pick a month + a cohort (a staff category, or All), then download
 * either:
 *   • ONE combined PDF (one payslip per page) — best for printing, or
 *   • a ZIP of individual payslip PDFs — best for filing/sharing.
 *
 * Files are named LKC_<cohort>_<period>, e.g. LKC_Teaching_2026-06.pdf
 * / .zip. Per-file names inside the zip are LKC_<employeeNo>_<period>.
 *
 * Payslips are assembled fresh from current payroll data (same as the
 * Pay Run preview), so this works whether or not they've been generated
 * to storage yet. Nothing is written — this is download-only.
 *
 * The combined PDF reuses renderPayslip (one page each); the zip reuses
 * buildPayslipPdf per slip. JSZip is dynamically imported so it is only
 * loaded when the zip is actually built.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import { FileDown, FileArchive, Loader2 } from 'lucide-react';
import { SoftCard, AccentButton, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildPayslipPdf, renderPayslip } from '@/lib/payslipPdf';
import {
  assemblePayslip, monthPeriod, periodLabel, type AssembledPayslip,
} from '@/lib/assemblePayslip';
import { STAFF_CATEGORY_LABELS, type StaffCategory } from '@/lib/hr';
import { fmtPula, MONTH_LABELS } from '@/lib/hrPayroll';
import type { LeaveRequestLike } from '@/lib/hrLeave';

type CohortKey = 'all' | StaffCategory;

interface SlipRow {
  staffId: string;
  employeeNo: string;
  name: string;
  assembled: AssembledPayslip;
}

/** Yield to the event loop so the progress modal can repaint mid-loop. */
const tick = () => new Promise<void>((r) => setTimeout(r, 0));

export default function BulkPayslips() {
  const data = useHrData();
  const { policies: leavePolicies } = useLeavePolicies();
  const disclaimer = data.payrollSettings?.payslipDisclaimer;

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [cohort, setCohort] = useState<CohortKey>('all');
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  const period = useMemo(() => monthPeriod(year, month), [year, month]);

  const rows: SlipRow[] = useMemo(() => {
    if (data.loading) return [];
    return data.staff
      .filter((s) => cohort === 'all' || s.category === cohort)
      .map((s) => {
        const profile = data.payrollProfiles.find((p) => p.staffId === s.id);
        if (!profile || (profile.basicSalary ?? 0) <= 0) return null;
        const deductions = data.deductions.filter((d) => d.staffId === s.id);
        const leave = data.leave.filter((l) => l.staffId === s.id) as LeaveRequestLike[];
        const allocation = data.leaveAllocations.find((a) => a.staffId === s.id);
        const reference = `SLIP/${year}-${String(month).padStart(2, '0')}/${s.employeeNo}`;
        const assembled = assemblePayslip({
          staff: s, profile, deductions, taxTable: data.activeTaxTable,
          leaveAllocation: allocation, leaveRequests: leave, period, reference,
          // Same leave source as the leave module — without the policies the
          // payslip resolves entitlements from the staff allocation alone.
          leavePolicies, leaveAccrualStart: data.payrollSettings?.leaveAccrualStart,
          sickBalancesFromMonth: data.payrollSettings?.sickBalancesFromMonth,
        });
        return { staffId: s.id, employeeNo: s.employeeNo, name: assembled.staffName, assembled };
      })
      .filter((r): r is SlipRow => !!r)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, cohort, period, year, month]);

  const netTotal = useMemo(() => rows.reduce((s, r) => s + r.assembled.net, 0), [rows]);

  const periodKey = `${year}-${String(month).padStart(2, '0')}`;
  const cohortName = cohort === 'all' ? 'AllStaff' : STAFF_CATEGORY_LABELS[cohort].replace(/\s+/g, '');
  const baseName = `LKC_${cohortName}_${periodKey}`;

  async function downloadCombined() {
    if (rows.length === 0 || busy) return;
    setBusy(true);
    setModal({ show: true, status: 'saving', message: `Building combined PDF… 0 of ${rows.length}` });
    try {
      const logo = await loadLogoDataUrl();
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      for (let i = 0; i < rows.length; i++) {
        if (i > 0) pdf.addPage();
        renderPayslip(pdf, rows[i].assembled, { logoDataUrl: logo, disclaimer });
        setModal({ show: true, status: 'saving', message: `Building combined PDF… ${i + 1} of ${rows.length}` });
        await tick();
      }
      pdf.save(`${baseName}.pdf`);
      setModal({ show: true, status: 'success', message: `Combined PDF ready — ${rows.length} payslip${rows.length > 1 ? 's' : ''}.` });
    } catch (e) {
      setModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function downloadZip() {
    if (rows.length === 0 || busy) return;
    setBusy(true);
    setModal({ show: true, status: 'saving', message: `Building ZIP… 0 of ${rows.length}` });
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      const logo = await loadLogoDataUrl();
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const pdf = buildPayslipPdf(r.assembled, { logoDataUrl: logo, disclaimer });
        zip.file(`LKC_${r.employeeNo}_${periodKey}.pdf`, pdf.output('blob'));
        setModal({ show: true, status: 'saving', message: `Building ZIP… ${i + 1} of ${rows.length}` });
        await tick();
      }
      const out = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(out);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseName}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setModal({ show: true, status: 'success', message: `ZIP ready — ${rows.length} payslip${rows.length > 1 ? 's' : ''}.` });
    } catch (e) {
      setModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  return (
    <div className="space-y-6">
      <SoftCard hover={false}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
                {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Year</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm w-24')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Cohort</label>
              <select value={cohort} onChange={(e) => setCohort(e.target.value as CohortKey)} className={glassFieldClass('py-1.5 text-sm')}>
                <option value="all">All staff</option>
                {(Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[]).map((c) => (
                  <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatChip tone="blue" value={String(rows.length)} label={`Payslips · ${periodLabel(period)}`} />
            <StatChip tone="emerald" value={fmtPula(netTotal)} label="Total net" />
            <StatChip tone="gold" value={baseName} label="File name" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <AccentButton icon={busy ? Loader2 : FileDown} disabled={busy || rows.length === 0} onClick={downloadCombined}>
              {busy ? 'Working…' : 'Combined PDF'}
            </AccentButton>
            <button
              onClick={downloadZip}
              disabled={busy || rows.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <FileArchive size={15} />}
              ZIP of individual PDFs
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Combined PDF is one payslip per page — best for bulk printing. The ZIP contains a
            separate PDF per staff member. Payslips reflect current payroll data for the selected
            month, whether or not they've been generated to storage yet.
          </p>
        </div>
      </SoftCard>

      <SaveConfirmation
        show={modal.show}
        status={modal.status}
        message={modal.message}
        onDismiss={() => setModal((m) => ({ ...m, show: false }))}
        autoDismissMs={3500}
      />
    </div>
  );
}
